import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { MoreHorizontal, Lock } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useMobileNavPrefs } from '@/hooks/useMobileNavPrefs'
import { useFeatureGate } from '@/hooks/useFeatureGate'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import type { NavItemDef } from '@/lib/navRegistry'

interface MobileBottomNavProps {
  badges?: Record<string, number>
}

export default function MobileBottomNav({ badges = {} }: MobileBottomNavProps) {
  const location = useLocation()
  const featureGate = useFeatureGate()
  const { pinnedItems, overflowItems } = useMobileNavPrefs()
  const [moreOpen, setMoreOpen] = useState(false)

  const isActivePath = (href: string) => {
    if (href === '/') return location.pathname === '/'
    if (href === '/settings') return location.pathname === '/settings'
    return location.pathname === href || location.pathname.startsWith(href + '/')
  }

  const renderTab = (item: NavItemDef) => {
    const Icon = item.icon
    const active = isActivePath(item.href)
    const badge = badges[item.href]
    const locked = Boolean(item.gatedBy) && !featureGate.isAllowed(item.gatedBy!)

    return (
      <Link
        key={item.key}
        to={item.href}
        className="relative flex-1 flex flex-col items-center justify-center gap-0.5 min-h-[56px] py-1 px-1 select-none active:scale-95 transition-transform"
      >
        <div
          className={cn(
            'relative flex h-9 w-12 items-center justify-center rounded-xl transition-all',
            active
              ? 'bg-gradient-brand shadow-sm shadow-brand-royal/30'
              : 'bg-transparent'
          )}
        >
          <Icon
            className={cn(
              'h-[22px] w-[22px] transition-colors',
              active ? 'text-white' : 'text-muted-foreground'
            )}
            strokeWidth={active ? 2.25 : 2}
          />
          {badge ? (
            <span className="absolute -top-1 -right-1 flex h-4 min-w-4 px-1 items-center justify-center rounded-full bg-red-500 text-[9px] font-bold text-white ring-2 ring-background">
              {badge > 99 ? '99+' : badge}
            </span>
          ) : null}
          {locked && !active && (
            <Lock className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 text-muted-foreground/70" />
          )}
        </div>
        <span
          className={cn(
            'text-[10px] font-medium leading-tight tracking-tight truncate max-w-full',
            active ? 'text-brand-royal' : 'text-muted-foreground'
          )}
        >
          {item.name}
        </span>
      </Link>
    )
  }

  return (
    <>
      <nav
        className={cn(
          'fixed bottom-0 left-0 right-0 z-40 md:hidden',
          'bg-card/95 backdrop-blur-xl border-t border-border/50',
          'pb-[env(safe-area-inset-bottom)]'
        )}
        aria-label="Primary mobile navigation"
      >
        <div className="flex items-stretch">
          {pinnedItems.map(renderTab)}
          <button
            type="button"
            onClick={() => setMoreOpen(true)}
            className="relative flex-1 flex flex-col items-center justify-center gap-0.5 min-h-[56px] py-1 px-1 select-none active:scale-95 transition-transform"
            aria-label="More navigation options"
          >
            <div
              className={cn(
                'relative flex h-9 w-12 items-center justify-center rounded-xl transition-all',
                moreOpen ? 'bg-gradient-brand shadow-sm shadow-brand-royal/30' : 'bg-transparent'
              )}
            >
              <MoreHorizontal
                className={cn(
                  'h-[22px] w-[22px] transition-colors',
                  moreOpen ? 'text-white' : 'text-muted-foreground'
                )}
                strokeWidth={moreOpen ? 2.25 : 2}
              />
            </div>
            <span
              className={cn(
                'text-[10px] font-medium leading-tight tracking-tight',
                moreOpen ? 'text-brand-royal' : 'text-muted-foreground'
              )}
            >
              More
            </span>
          </button>
        </div>
      </nav>

      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent
          side="bottom"
          className="rounded-t-2xl p-0 max-h-[80vh] overflow-y-auto"
        >
          <SheetHeader className="px-5 pt-5 pb-3 text-left">
            <SheetTitle className="text-base font-heading font-semibold">More</SheetTitle>
          </SheetHeader>
          <div className="grid grid-cols-3 gap-2 px-4 pb-6">
            {overflowItems.map(item => {
              const Icon = item.icon
              const active = isActivePath(item.href)
              const badge = badges[item.href]
              const locked = Boolean(item.gatedBy) && !featureGate.isAllowed(item.gatedBy!)
              return (
                <Link
                  key={item.key}
                  to={item.href}
                  onClick={() => setMoreOpen(false)}
                  className={cn(
                    'relative flex flex-col items-center justify-center gap-2 rounded-xl border border-border/60 bg-card px-2 py-4 active:scale-[0.97] transition-transform',
                    active && 'border-brand-royal/40 bg-brand-ice/40 dark:bg-brand-royal/10'
                  )}
                >
                  <div
                    className={cn(
                      'relative flex h-11 w-11 items-center justify-center rounded-xl',
                      active ? 'bg-gradient-brand' : 'bg-brand-ice dark:bg-brand-royal/15'
                    )}
                  >
                    <Icon
                      className={cn(
                        'h-5 w-5',
                        active ? 'text-white' : 'text-brand-royal'
                      )}
                    />
                    {badge ? (
                      <span className="absolute -top-1 -right-1 flex h-4 min-w-4 px-1 items-center justify-center rounded-full bg-red-500 text-[9px] font-bold text-white ring-2 ring-background">
                        {badge > 99 ? '99+' : badge}
                      </span>
                    ) : null}
                    {locked && (
                      <Lock className="absolute -bottom-0.5 -right-0.5 h-3 w-3 text-muted-foreground/70 bg-background rounded-full p-0.5" />
                    )}
                  </div>
                  <span className="text-xs font-medium text-center leading-tight">
                    {item.name}
                  </span>
                </Link>
              )
            })}
          </div>
          <div className="px-5 pb-6 pt-2 border-t border-border/40">
            <Link
              to="/settings"
              onClick={() => setMoreOpen(false)}
              className="block text-center text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              Customize bottom nav in Settings →
            </Link>
          </div>
        </SheetContent>
      </Sheet>
    </>
  )
}
